import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

interface QueryCondition {
  id: string;
  field: string;
  operator: string;
  value: string;
}

interface QueryFilter {
  entityTypes: string[];
  relationshipTypes: string[];
  maxDepth: number;
  maxResults: number;
  timeRange: string;
}

export interface GraphQuery {
  id?: string;
  name: string;
  description?: string;
  rootEntity?: string;
  startEntity: string;
  conditions: QueryCondition[];
  filters: QueryFilter;
  rawQuery?: string;
}

interface GraphData {
  nodes: unknown[];
  links: unknown[];
}

interface QueryResult {
  queryId: string;
  data: GraphData;
  timestamp: string;
  executionTimeMs: number;
  nodeCount: number;
  linkCount: number;
}

interface GraphQueryState {
  queryResults: QueryResult | null;
  savedQueries: GraphQuery[];
  recentQueries: QueryResult[];
  loading: boolean;
  error: string | null;
}

const initialState: GraphQueryState = {
  queryResults: null,
  savedQueries: [],
  recentQueries: [],
  loading: false,
  error: null
};

// Async thunk for executing a graph query
export const executeGraphQuery = createAsyncThunk<GraphData, GraphQuery>(
  'graphQuery/executeGraphQuery',
  async (query: GraphQuery, { rejectWithValue }) => {
    try {
      // NOTE: Backend graph query endpoints are not yet available.
      // Temporary mock response until backend implementation.
      // Expected endpoint: POST /api/graph/query
      const response = { data: { nodes: [], links: [] } };
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        axiosError.response?.data?.message || 'Failed to execute graph query'
      );
    }
  }
);

// Async thunk for saving a query
export const saveQuery = createAsyncThunk<GraphQuery, GraphQuery>(
  'graphQuery/saveQuery',
  async (query: GraphQuery, { rejectWithValue }) => {
    try {
      // NOTE: Backend graph query endpoints are not yet available.
      // Temporary mock response until backend implementation.
      // Expected endpoint: POST /api/graph/query/save
      const response = { data: { id: Date.now().toString() } };
      return { ...query, id: response.data.id };
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        axiosError.response?.data?.message || 'Failed to save query'
      );
    }
  }
);

// Async thunk for loading saved queries
export const loadSavedQueries = createAsyncThunk<GraphQuery[]>(
  'graphQuery/loadSavedQueries',
  async (_, { rejectWithValue }) => {
    try {
      // NOTE: Backend graph query endpoints are not yet available.
      // Temporary mock response until backend implementation.
      // Expected endpoint: GET /api/graph/query/saved
      const response = { data: [] };
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        axiosError.response?.data?.message || 'Failed to load saved queries'
      );
    }
  }
);

// Async thunk for loading recent query results
export const loadRecentQueries = createAsyncThunk<QueryResult[]>(
  'graphQuery/loadRecentQueries',
  async (_, { rejectWithValue }) => {
    try {
      // NOTE: Backend graph query endpoints are not yet available.
      // Temporary mock response until backend implementation.
      // Expected endpoint: GET /api/graph/query/recent
      const response = { data: [] };
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        axiosError.response?.data?.message || 'Failed to load recent queries'
      );
    }
  }
);

// Async thunk for deleting a saved query
export const deleteQuery = createAsyncThunk<string, string>(
  'graphQuery/deleteQuery',
  async (queryId: string, { rejectWithValue }) => {
    try {
      // NOTE: Backend graph query endpoints are not yet available.
      // Temporary mock - no actual deletion until backend implementation.
      // Expected endpoint: DELETE /api/graph/query/saved/{id}
      return queryId;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        axiosError.response?.data?.message || 'Failed to delete query'
      );
    }
  }
);

const graphQuerySlice = createSlice({
  name: 'graphQuery',
  initialState,
  reducers: {
    clearQueryResults: (state) => {
      state.queryResults = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle executeGraphQuery
      .addCase(executeGraphQuery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(executeGraphQuery.fulfilled, (state, action) => {
        state.loading = false;
        
        // Create a QueryResult object from the GraphData payload
        const queryResult: QueryResult = {
          queryId: Date.now().toString(), // Generate a unique ID
          data: action.payload,
          timestamp: new Date().toISOString(),
          executionTimeMs: 0, // This would ideally come from the API
          nodeCount: action.payload.nodes.length,
          linkCount: action.payload.links.length
        };
        
        state.queryResults = queryResult;
        
        // Add to recent queries
        state.recentQueries = [
          queryResult,
          ...state.recentQueries.slice(0, 9) // Keep only 10 most recent
        ];
      })
      .addCase(executeGraphQuery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle saveQuery
      .addCase(saveQuery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveQuery.fulfilled, (state, action) => {
        state.loading = false;
        
        const savedQuery = action.payload as GraphQuery;
        
        // Add to saved queries if not already present
        const existingIndex = state.savedQueries.findIndex(q => q.name === savedQuery.name);
        if (existingIndex >= 0) {
          state.savedQueries[existingIndex] = savedQuery;
        } else {
          state.savedQueries.push(savedQuery);
        }
      })
      .addCase(saveQuery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle loadSavedQueries
      .addCase(loadSavedQueries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSavedQueries.fulfilled, (state, action) => {
        state.loading = false;
        state.savedQueries = action.payload as GraphQuery[];
      })
      .addCase(loadSavedQueries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle loadRecentQueries
      .addCase(loadRecentQueries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadRecentQueries.fulfilled, (state, action) => {
        state.loading = false;
        state.recentQueries = action.payload as QueryResult[];
      })
      .addCase(loadRecentQueries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle deleteQuery
      .addCase(deleteQuery.fulfilled, (state, action) => {
        const queryId = action.payload as string;
        state.savedQueries = state.savedQueries.filter(
          query => query.id !== queryId
        );
      });
  }
});

export const { clearQueryResults } = graphQuerySlice.actions;
export default graphQuerySlice.reducer;
